/**
 * Sign-in, the OIDC callback, activation, and Home.
 *
 * Home is here rather than with a domain because it belongs to none of them: it is the screen
 * every principal lands on, and what it shows is decided by scope and preset, not by module.
 *
 * The other three are the app's whole unauthenticated surface, which is why they sit together: a
 * reader asking *what can somebody reach without signing in* has one file to read.
 */

import type { RouteManifestEntry } from '../route-manifest.js';

export const AUTH_ROUTES: readonly RouteManifestEntry[] = [
  {
    id: 'sign-in',
    path: '/sign-in',
    entitlement: null,
    entitlementExemption:
      'Authentication precedes entitlement. Gating the sign-in screen on a module would make a ' +
      'tenant with a misconfigured entitlement set unable to sign in and discover that fact.',
    authenticated: false,
    load: () => import('../screens/sign-in.js'),
  },
  {
    id: 'auth-callback',
    path: '/auth/callback',
    entitlement: null,
    entitlementExemption:
      'The redirect target of the OIDC flow. It runs before a session exists, so there are no ' +
      'entitlements to read yet.',
    authenticated: false,
    load: () => import('../screens/callback.js'),
  },
  {
    /*
     * Accepting an invitation — 20 Aug 2026, and the only unauthenticated screen that *does*
     * something rather than redirecting.
     *
     * `$userAccountId` matches the id `outreach` puts in the invitation link and the path parameter
     * of `identity.invitation.activate`. Three places, no shared type between them — the app is a
     * separate build from the API and from the module that composes the mail — so
     * `modules/outreach/src/consumers/messages.test.ts` asserts the exact string this route
     * expects. Renaming this path without that test failing would be a link nobody could open.
     */
    id: 'activate',
    path: '/activate/$userAccountId',
    entitlement: null,
    entitlementExemption:
      'Accepting an invitation precedes every entitlement, exactly as signing in does. The person ' +
      'has no account yet, so there is no tenant whose entitlement set could be read.',
    authenticated: false,
    load: () => import('../screens/activate.js'),
  },
  {
    /*
     * Where a reset email lands — M-21.6.
     *
     * The same screen module as `/sign-in`, deliberately. It is one surface to the person using
     * it: the same panel, the same stage, the same concept drawn for this browser, and no visible
     * jump between asking for a link and using one. The screen reads the token from the query
     * string and opens on the choose-a-password view.
     *
     * The path is built by `identity` as `${authAppBaseUrl()}/reset-password`
     * (`modules/identity/src/routes/auth.ts`), and the two have no shared type — the app is a
     * separate build from the API. Changing this string without changing that one is a link
     * nobody can open, which is the same trap `/activate/$userAccountId` documents above.
     */
    id: 'reset-password',
    path: '/reset-password',
    entitlement: null,
    entitlementExemption:
      'Setting a new password precedes every entitlement, exactly as signing in does. The person ' +
      'holds no session — that is why they are here — so there is no tenant whose entitlement ' +
      'set could be read.',
    authenticated: false,
    load: () => import('../screens/sign-in.js'),
  },
  {
    id: 'home',
    path: '/',
    entitlement: null,
    entitlementExemption:
      'The signed-in landing screen. It reports the session — including which entitlements the ' +
      'tenant has — so gating it on one would hide the only screen that could explain the ' +
      'configuration. Becomes the Action Center (module-feature-breakdown screen 3), which is ' +
      'cross-module by design and gated per widget rather than per route.',
    authenticated: true,
    // The one sectionless item, pinned above the three headings. See `NAV_SECTIONS`.
    nav: { labelKey: 'shell.nav.home', section: null, order: 0 },
    load: () => import('../screens/home.js'),
  },
];
